import React from 'react';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { showToast } from '@klpkonsern/ui';

import { Loader } from '@klp/design';
import type { KostnadskjemaUserInput } from '@klp/prosjekt/services/types';
import { useProsjektInvalidateTags } from '@klp/prosjekt/services/rtkApi';
import { Skeleton } from '@klp/layout/Skeleton';
import type { WithSubComponents } from '@forvalter/utils/TypeScriptUtils';
import { useKostnadsForm } from './useKostnadsForm';
import { useKostnadsKalkyleContext } from '../context/KostnadsKalkyleProvider';
import { KostnadsKalkyleFormGeneralData } from './KostnadsKalkyleFormGeneralData';
import { KostnadsKalkyleFormTable } from './KostnadsTable/KostnadsKalkyleFormTable';
import { NokkeltallSammendrag } from '../Nokkeltall/NokkeltallSammendrag';
import { TilleggSammendrag } from './KostnadsTable/TilleggSammendrag';
import { KostnadsKalkyleFormSubmitButton } from './KostnadsKalkyleFormSubmitButton';
import { KostnadsKalkyleFormAutosave } from './KostnadsKalkyleFormAutosave';

export type KostnadsKalkyleFormSubComponents = {
    GeneralData: typeof KostnadsKalkyleFormGeneralData;
    Table: typeof KostnadsKalkyleFormTable;
    TilleggSammendrag: typeof TilleggSammendrag;
    NokkeltallSammendrag: typeof NokkeltallSammendrag;
    SubmitButton: typeof KostnadsKalkyleFormSubmitButton;
    Autosave: typeof KostnadsKalkyleFormAutosave;
};

export const KostnadsKalkyleForm: WithSubComponents<
    React.FC<PropsWithChildren<unknown>>,
    KostnadsKalkyleFormSubComponents
> = ({ children }) => {
    const { t } = useTranslation();
    const { validationSchema } = useKostnadsForm();

    const invalidateKostnadskalkyle = useProsjektInvalidateTags([
        'Kostnadskalkyle',
    ]);

    const {
        createKostnadskjema,
        shouldInvalidateVurderingsskjema,
        isKostnadSkjemaLoading,
        toggleModal,
        initialValues,
    } = useKostnadsKalkyleContext();

    const handleSubmit = async (values: KostnadskjemaUserInput) => {
        await createKostnadskjema({
            ...values,
            shouldInvalidateVurderingsskjema,
        })
            .unwrap()
            .then(() => {
                showToast({
                    body: t('prosjekt.successfulOperation'),
                    type: 'success',
                });
            })
            .catch((_error) => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    if (!initialValues) {
        return <Loader isLoading={true} />;
    }

    return (
        <Skeleton isLoading={isKostnadSkjemaLoading}>
            <Formik<KostnadskjemaUserInput>
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize={true}
                onSubmit={async (value, { setSubmitting }) => {
                    await handleSubmit(value);
                    setSubmitting(false);
                    toggleModal(false);
                    invalidateKostnadskalkyle();
                }}
            >
                <Form>
                    <>{children}</>
                </Form>
            </Formik>
        </Skeleton>
    );
};

KostnadsKalkyleForm.GeneralData = KostnadsKalkyleFormGeneralData;
KostnadsKalkyleForm.Table = KostnadsKalkyleFormTable;
KostnadsKalkyleForm.TilleggSammendrag = TilleggSammendrag;
KostnadsKalkyleForm.NokkeltallSammendrag = NokkeltallSammendrag;
KostnadsKalkyleForm.SubmitButton = KostnadsKalkyleFormSubmitButton;
KostnadsKalkyleForm.Autosave = KostnadsKalkyleFormAutosave;
